/**
 * ============================================
 * USE API ERROR - Hook de traducción de errores API
 * ============================================
 *
 * Hook para traducir códigos de error del backend a mensajes
 * localizados según el idioma seleccionado.
 *
 * USO:
 * const { getErrorMessage } = useApiError();
 *
 * try {
 *   await api.post('/endpoint', data);
 * } catch (error) {
 *   const message = getErrorMessage(error);
 *   setError(message);
 * }
 */

import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";

/**
 * Estructura de error del backend
 */
interface ApiErrorResponse {
  error_code?: string;
  detail?: string;
  message?: string;
}

/**
 * Mapeo de códigos de error a claves de traducción
 */
const ERROR_CODE_MAP: Record<string, string> = {
  // Errores de autenticación
  INVALID_CREDENTIALS: "api.invalidCredentials",
  ACCOUNT_LOCKED: "api.accountLocked",
  INACTIVE_USER: "api.inactiveUser",
  TOO_MANY_ATTEMPTS: "api.tooManyAttempts",
  TOKEN_EXPIRED: "api.tokenExpired",
  TOKEN_INVALID: "api.tokenInvalid",
  TOKEN_BLACKLISTED: "api.tokenBlacklisted",

  // Errores de validación
  VALIDATION_ERROR: "api.validationError",
  DUPLICATE_EMAIL: "api.duplicateEmail",
  DUPLICATE_DOCUMENT: "api.duplicateDocument",
  INVALID_EMAIL: "api.invalidEmail",
  WEAK_PASSWORD: "api.weakPassword",

  // Errores de permisos
  PERMISSION_DENIED: "api.permissionDenied",
  UNAUTHORIZED: "api.unauthorized",
  FORBIDDEN: "api.forbidden",

  // Errores de recursos
  NOT_FOUND: "api.notFound",
  RESOURCE_NOT_FOUND: "api.resourceNotFound",
  USER_NOT_FOUND: "api.userNotFound",
  ROLE_NOT_FOUND: "api.roleNotFound",

  // Errores de negocio
  ROLE_HAS_USERS: "api.roleHasUsers",
  CAI_EXPIRED: "api.caiExpired",
  CAI_RANGE_EXHAUSTED: "api.caiRangeExhausted",
  INVALID_RTN: "api.invalidRtn",

  // Errores de edición de órdenes
  orden_no_editable: "ordenes.edit.notEditable.generic",
  orden_no_modificable: "ordenes.edit.notEditable.baja",
  order_has_payments: "ordenes.errors.orderHasPayments",

  // Errores de facturación multi-orden
  empty_order_list: "facturacion.errors.empty_order_list",
  orden_not_found: "facturacion.errors.orden_not_found",
  inconsistent_patients: "facturacion.errors.inconsistent_patients",
  inconsistent_branches: "facturacion.errors.inconsistent_branches",
  inconsistent_clients: "facturacion.errors.inconsistent_clients",
  order_already_invoiced: "facturacion.errors.order_already_invoiced",
  client_name_required: "facturacion.errors.client_name_required",
  invalid_rtn_format: "facturacion.errors.invalid_rtn_format",
  orden_ya_pagada_directo: "facturacion.errors.orden_ya_pagada_directo",
  orden_en_factura: "facturacion.errors.orden_en_factura",
  orden_sin_pago_directo: "facturacion.errors.orden_sin_pago_directo",
  order_has_partial_payments: "facturacion.errors.order_has_partial_payments",

  // Errores generales
  SERVER_ERROR: "api.serverError",
  NETWORK_ERROR: "api.networkError",
  UNKNOWN_ERROR: "api.unknownError",

  // Errores de búsqueda avanzada
  search_params_required: "busquedaAvanzada.errors.paramsRequired",
  search_min_length: "busquedaAvanzada.errors.minLength",
  invalid_date_range: "busquedaAvanzada.errors.invalidDateRange",
};

/**
 * Hook para traducir errores de API
 */
export function useApiError() {
  const { t } = useTranslation();

  /**
   * Extrae información del error de Axios
   */
  const parseAxiosError = (error: AxiosError<ApiErrorResponse & Record<string, unknown>>) => {
    const response = error.response;

    if (!response) {
      // Error de red (sin respuesta del servidor)
      return {
        errorCode: "NETWORK_ERROR",
        detail: null,
        status: 0,
        data: null,
      };
    }

    const data = response.data;
    return {
      errorCode: data?.error_code || null,
      detail: data?.detail || data?.message || null,
      status: response.status,
      data: data || null,
    };
  };

  /**
   * Obtiene el mensaje de error traducido
   *
   * Prioridad:
   * 1. Si hay error_code conocido → traducción correspondiente
   * 2. Si hay errores de campo específicos → concatenar mensajes
   * 3. Si hay detail del backend → usar como fallback
   * 4. Si hay código HTTP conocido → mensaje genérico por código
   * 5. Mensaje de error genérico
   */
  const getErrorMessage = (error: unknown): string => {
    // Si no es un error de Axios, retornar mensaje genérico
    if (!error || typeof error !== "object" || !("isAxiosError" in error)) {
      // Verificar si es un error con response (estructura de Axios sin isAxiosError)
      if (error && typeof error === "object" && "response" in error) {
        const axiosLikeError = error as {
          response?: { data?: ApiErrorResponse & Record<string, unknown>; status?: number };
        };
        const data = axiosLikeError.response?.data;
        const status = axiosLikeError.response?.status;

        // Intentar traducir por error_code
        if (data?.error_code && ERROR_CODE_MAP[data.error_code]) {
          return t(ERROR_CODE_MAP[data.error_code]);
        }

        // Buscar errores de campo específicos (ej: { num_documento: ["mensaje"] })
        if (data && typeof data === "object") {
          const fieldErrors = extractFieldErrors(data);
          if (fieldErrors) {
            return fieldErrors;
          }
        }

        // Usar detail como fallback
        if (data?.detail) {
          return data.detail;
        }

        // Mensaje por código HTTP
        if (status) {
          return getHttpStatusMessage(status);
        }
      }

      return t("api.unknownError");
    }

    const axiosError = error as AxiosError<ApiErrorResponse & Record<string, unknown>>;
    const { errorCode, detail, status, data } = parseAxiosError(axiosError);

    // 1. Traducción por error_code
    if (errorCode && ERROR_CODE_MAP[errorCode]) {
      return t(ERROR_CODE_MAP[errorCode]);
    }

    // 2. Buscar errores de campo específicos
    if (data) {
      const fieldErrors = extractFieldErrors(data);
      if (fieldErrors) {
        return fieldErrors;
      }
    }

    // 3. Fallback al detail del backend
    if (detail) {
      return detail;
    }

    // 4. Mensaje por código HTTP
    if (status) {
      return getHttpStatusMessage(status);
    }

    // 5. Mensaje genérico
    return t("api.unknownError");
  };

  /**
   * Extrae mensajes de error de campos específicos del response
   */
  const extractFieldErrors = (data: Record<string, unknown>): string | null => {
    const errorMessages: string[] = [];

    for (const [key, value] of Object.entries(data)) {
      // Ignorar campos conocidos que no son errores de campo
      if (["error_code", "detail", "message"].includes(key)) {
        continue;
      }

      // Función helper para extraer mensaje de un valor
      const extractMessage = (v: unknown): string | null => {
        if (typeof v === "string") {
          return v;
        }
        if (v && typeof v === "object") {
          const errorObj = v as { string?: string; message?: string };
          if (errorObj.string) {
            return errorObj.string;
          }
          if (errorObj.message) {
            return errorObj.message;
          }
          // Intentar extraer el mensaje del toString para ErrorDetail de DRF
          const str = String(v);
          // Limpiar formato ErrorDetail(string='...', code='...')
          const match = str.match(/string='([^']+)'/);
          if (match) {
            return match[1];
          }
          // Si no matchea el formato ErrorDetail, usar el string directamente
          // pero solo si no es "[object Object]"
          if (str && str !== "[object Object]") {
            return str;
          }
        }
        return null;
      };

      // Manejar non_field_errors (errores de validación a nivel de modelo)
      if (key === "non_field_errors" && Array.isArray(value)) {
        for (const v of value) {
          const msg = extractMessage(v);
          if (msg) errorMessages.push(msg);
        }
        continue;
      }

      // Si el valor es un array, son mensajes de error
      if (Array.isArray(value)) {
        for (const v of value) {
          const msg = extractMessage(v);
          if (msg) errorMessages.push(msg);
        }
      } else {
        const msg = extractMessage(value);
        if (msg) errorMessages.push(msg);
      }
    }

    return errorMessages.length > 0 ? errorMessages.join(" ") : null;
  };

  /**
   * Obtiene mensaje genérico por código HTTP
   */
  const getHttpStatusMessage = (status: number): string => {
    switch (status) {
      case 400:
        return t("api.badRequest");
      case 401:
        return t("api.unauthorized");
      case 403:
        return t("api.forbidden");
      case 404:
        return t("api.notFound");
      case 409:
        return t("api.conflict");
      case 422:
        return t("api.validationError");
      case 429:
        return t("api.tooManyRequests");
      case 500:
        return t("api.serverError");
      case 502:
      case 503:
      case 504:
        return t("api.serviceUnavailable");
      default:
        return t("api.unknownError");
    }
  };

  /**
   * Verifica si el error es de un tipo específico
   */
  const isErrorCode = (error: unknown, code: string): boolean => {
    if (!error || typeof error !== "object") return false;

    if ("response" in error) {
      const axiosLikeError = error as { response?: { data?: ApiErrorResponse } };
      return axiosLikeError.response?.data?.error_code === code;
    }

    return false;
  };

  return {
    getErrorMessage,
    getHttpStatusMessage,
    isErrorCode,
  };
}
